import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'styled-bootstrap-grid';

import { useTranslations } from '@lang';
import { useFormContext, useWatch } from 'react-hook-form';

import Paragraph from '@common/components/Paragraph';
import { Button } from '@common/components/Button';
import { StickyBarContent } from '@common/components/StickyBar';
import Portal from '@common/components/Portal';

import { RHFExtras } from '@common/components/SpaEntity/components/RHFExtras';
import { Heading4, Title1 } from '@common/components/Typography';
import { Subheading } from '@common/components/BookingForms';
import { SelectionGroups } from './SelectionGroups';
import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';

const StyledContainer = styled(Container)`
  max-width: 968px;
  margin-left: 0;
`;

const SectionTitleWrapper = styled.div`
  margin: 30px 0;
`;

type CustomiseStepProps = {
  spaEntity: any;
  stickyBarId: string;
  onSubmit: (...args: any[]) => any;
  freeExtras?: any[];
  paidExtras?: any[];
  handleInvalidConfig: (...args: any[]) => any;
};

export const CustomiseStep = ({
  spaEntity,
  stickyBarId,
  onSubmit,
  freeExtras = [],
  paidExtras = [],
  handleInvalidConfig,
}: CustomiseStepProps) => {
  const { t } = useTranslations();
  const { handleSubmit } = useFormContext<SpaEntityFormValues>();
  const selectedGuests = useWatch({ name: 'guests' });
  const selectedArrivalTime = useWatch({ name: 'arrivalTime' });
  const selectedExtras = useWatch({ name: 'extras' });

  useEffect(() => {
    if (!selectedGuests || !selectedArrivalTime) {
      handleInvalidConfig();
    }
  }, [selectedGuests, selectedArrivalTime, handleInvalidConfig]);

  const getCalculatedPrice = () => {
    const numberOfGuests = selectedGuests?.length ?? 1;
    const baseAmount = selectedArrivalTime?.price?.amount || 0;
    let extrasAmount = 0;

    if (selectedExtras) {
      extrasAmount = Object.values(selectedExtras)
        .map((extra: any) =>
          extra ? extra.reduce((total, current) => total + current.price.amount, 0) : 0
        )
        .reduce((total: number, current: number) => total + current, 0);
    }

    const calculatedAmount = baseAmount * numberOfGuests + extrasAmount;

    return {
      ...selectedArrivalTime?.price,
      amount: calculatedAmount,
    };
  };

  const getCalculatedDiscount = () => {
    if (!selectedArrivalTime?.discount) {
      return undefined;
    }
    const numberOfGuests = selectedGuests?.length ?? 1;
    const baseAmount = selectedArrivalTime.discount.discountedPrice.amount;
    let extrasAmount = 0;

    if (selectedExtras) {
      extrasAmount = Object.values(selectedExtras)
        .map((extra: any) =>
          extra ? extra.reduce((total, current) => total + current.price.amount, 0) : 0
        )
        .reduce((total: number, current: number) => total + current, 0);
    }

    const calculatedAmount = baseAmount * numberOfGuests + extrasAmount;

    return {
      discountedPrice: {
        ...selectedArrivalTime.discount.discountedPrice,
        amount: calculatedAmount,
      },
    };
  };

  return selectedGuests ? (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <StyledContainer fluid>
        <Row>
          <Col col>
            <Heading4 tag="h1">{t('treatment.customiseYourExperience')}</Heading4>
            {spaEntity.selectionGroups?.length > 0 && (
              <div>
                <SectionTitleWrapper>
                  <Title1 tag="h2">{t('treatment.includedOptions')}</Title1>
                  <Paragraph>{t('treatment.optionsIncludedInSpaDay')}</Paragraph>
                </SectionTitleWrapper>
                <SelectionGroups
                  selectionGroups={spaEntity.selectionGroups}
                  selectedGuests={selectedGuests}
                />
              </div>
            )}
          </Col>
        </Row>
        {freeExtras?.length > 0 && (
          <Row>
            <Col col>
              <Subheading>{t('treatment.freeExtrasHeader')}</Subheading>
              <Container fluid>
                <RHFExtras
                  extras={freeExtras}
                  selectedGuests={selectedGuests}
                  selectedExtras={selectedExtras}
                  showPrice
                />
              </Container>
            </Col>
          </Row>
        )}
        {paidExtras?.length > 0 && (
          <Row>
            <Col col>
              <Title1 tag="h2">{t('treatment.extrasAndAdditions')}</Title1>
              <Container fluid>
                <RHFExtras
                  extras={paidExtras}
                  selectedGuests={selectedGuests}
                  selectedExtras={selectedExtras}
                  showPrice
                />
              </Container>
            </Col>
          </Row>
        )}
      </StyledContainer>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice()}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button isPrimary type="submit" onClick={handleSubmit(onSubmit)}>
              {t('booking.continue')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
